import {
  CreateCollectionRequest,
  CreateCollectionResult,
  UpdateCollectionRequest,
  UpdateCollectionResult,
  normalizeCollectionName,
  parseCreateCollectionResult,
  parseUpdateCollectionResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import {
  CollectionRepository,
  firebaseCollectionRepository,
} from '../../repositories/collectionRepository';
import { createCollection, updateCollection } from '../../firebase/functions';

export interface CollectionManagementDependencies {
  repository: CollectionRepository;
  createOperationId(): string;
  create(request: CreateCollectionRequest): Promise<CreateCollectionResult>;
  update(request: UpdateCollectionRequest): Promise<UpdateCollectionResult>;
}
const defaultDependencies = (): CollectionManagementDependencies => ({
  repository: firebaseCollectionRepository,
  createOperationId,
  create: async (request) =>
    parseCreateCollectionResult((await createCollection(request)).data),
  update: async (request) =>
    parseUpdateCollectionResult((await updateCollection(request)).data),
});

export const createCollectionManagementController = (
  dependencies: CollectionManagementDependencies = defaultDependencies(),
) => {
  let pendingCreate:
    { key: string; request: CreateCollectionRequest } | undefined;
  let pendingUpdate:
    { key: string; request: UpdateCollectionRequest } | undefined;
  return {
    async create(rawName: string) {
      const { name } = normalizeCollectionName(rawName);
      if (pendingCreate?.key !== name)
        pendingCreate = {
          key: name,
          request: { operationId: dependencies.createOperationId(), name },
        };
      return dependencies.create(pendingCreate.request);
    },
    async update(
      collectionId: string,
      input: UpdateCollectionRequest['patch'],
    ) {
      const patch = {
        ...input,
        ...(input.name !== undefined
          ? { name: normalizeCollectionName(input.name).name }
          : {}),
      };
      const key = JSON.stringify([collectionId, patch]);
      if (pendingUpdate?.key !== key) {
        const collection = await dependencies.repository.get(collectionId);
        if (!collection || collection.lifecycle !== 'active')
          throw new Error('The collection is unavailable.');
        pendingUpdate = {
          key,
          request: {
            collectionId,
            operationId: dependencies.createOperationId(),
            patch,
            expectedRevision: collection.revision,
            expectedLifecycleVersion: collection.lifecycleVersion,
          },
        };
      }
      const pending = pendingUpdate;
      try {
        const result = await dependencies.update(pending.request);
        if (result.collectionId !== collectionId)
          throw new Error('The collection update result is invalid.');
        return result;
      } catch (error) {
        if (
          nestedContractCode(error) === 'operation-conflict' &&
          pendingUpdate === pending
        )
          pendingUpdate = undefined;
        throw error;
      }
    },
    acknowledgeCreate() {
      pendingCreate = undefined;
    },
    acknowledgeUpdate() {
      pendingUpdate = undefined;
    },
    reset() {
      pendingCreate = undefined;
      pendingUpdate = undefined;
    },
  };
};

const nestedContractCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const candidate = error as {
    code?: unknown;
    details?: { code?: unknown; message?: unknown };
    cause?: unknown;
    message?: unknown;
  };
  if (typeof candidate.details?.code === 'string')
    return candidate.details.code;
  const causeCode = nestedContractCode(candidate.cause, depth + 1);
  if (causeCode) return causeCode;
  if (typeof candidate.code === 'string') return candidate.code;
  return undefined;
};

export const collectionManagementErrorMessage = (error: unknown): string => {
  const code = nestedContractCode(error);
  const messageStr =
    error instanceof Error
      ? error.message
      : typeof (error as { message?: unknown })?.message === 'string'
        ? (error as { message: string }).message
        : '';
  const detailsMsg =
    typeof (error as { details?: { message?: unknown } })?.details?.message ===
    'string'
      ? (error as { details: { message: string } }).details.message
      : '';
  if (
    code?.includes('role-required') ||
    code?.includes('membership-required')
  ) {
    return 'Owner access is required for this collection change.';
  }
  if (
    code?.includes('verification-required') ||
    code?.includes('permission-denied') ||
    /verification/i.test(messageStr) ||
    /verification/i.test(detailsMsg)
  ) {
    return 'Email verification is required before creating a collection.';
  }
  if (code?.includes('operation-conflict')) {
    return 'The collection changed. Refresh and try again.';
  }
  if (code?.includes('collection-unavailable') || code?.includes('not-found')) {
    return 'The collection is currently unavailable.';
  }
  if (code?.includes('invalid-request')) {
    return 'Enter a collection name between 1 and 80 characters.';
  }
  if (error instanceof Error && /already has this name/i.test(error.message)) {
    return error.message;
  }
  if (
    error instanceof Error &&
    /already has this GPS location/i.test(error.message)
  ) {
    return error.message;
  }
  if (error instanceof Error && /1 to 80 characters/i.test(error.message)) {
    return 'Enter a collection name between 1 and 80 characters.';
  }
  return 'Unable to save the collection. Check your connection and try again.';
};
